import { matchPath, useLocation } from 'quartz-routing/react-router';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Paths, SEARCH_RESULTS_PROXY_PATHS } from '../constants/paths';
import { jsx as _jsx } from "react/jsx-runtime";
const LocationHistoryContext = /*#__PURE__*/createContext({
  getBackLinkLocation: () => undefined
});

/**
 * Checks if a pathname is a search results proxy path.
 * Proxy paths (like /apps/all) redirect to /explore with filters and should be skipped.
 */
function isSearchResultsProxyPath(location) {
  if (location.pathname === Paths.SEARCH_RESULTS && !location.search.includes('eco_search=')) {
    return true;
  }
  return SEARCH_RESULTS_PROXY_PATHS.some(path => matchPath(location.pathname, {
    path,
    exact: true
  }));
}

/**
 * Checks if a pathname is a listing detail page.
 * Listing pages should never be used as back link destinations.
 */
function isListingDetailPath(pathname) {
  return !!matchPath(pathname, {
    path: Paths.LISTING_DETAILS,
    exact: true
  });
}
export function LocationHistoryProvider({
  children
}) {
  const location = useLocation();

  // Initialize history, skipping proxy paths
  const [_history, setHistory] = useState(() => {
    if (isSearchResultsProxyPath(location)) {
      return [];
    }
    return [location];
  });
  useEffect(() => {
    setHistory(prevHistory => {
      // Skip proxy paths entirely - they redirect and shouldn't be in history
      if (isSearchResultsProxyPath(location)) {
        return prevHistory;
      }
      if (prevHistory.length === 0) {
        return [location];
      }
      const currentLocation = prevHistory[prevHistory.length - 1];

      // Skip if location hasn't actually changed (avoids duplicate entries)
      if (currentLocation.pathname === location.pathname && currentLocation.search === location.search) {
        return prevHistory;
      }

      // Detect back navigation: when the new location matches the second-to-last entry,
      // the user pressed the browser back button. Remove the last entry to maintain accuracy.
      if (prevHistory.length >= 2) {
        const secondToLast = prevHistory[prevHistory.length - 2];
        if (secondToLast.pathname === location.pathname && secondToLast.search === location.search) {
          return prevHistory.slice(0, -1);
        }
      }

      // Forward navigation: add new location to history
      return [...prevHistory, location];
    });
  }, [location]);

  // Returns the last location suitable for back links.
  // Skips listing detail pages and same-pathname entries so the back
  // breadcrumb always points to a genuinely different page. When no
  // such page exists, returns undefined so callers can fall back to a
  // default back path (e.g. the product homepage).
  const getBackLinkLocation = useCallback(() => {
    if (_history.length === 0) {
      return undefined;
    }

    // Use the actual router location rather than the last history entry.
    // Some pages (e.g. /solutions/:slug) match proxy path patterns and are excluded
    // from history tracking, so the last history entry may not reflect the current page.
    const currentPathname = location.pathname;
    for (let i = _history.length - 1; i >= 0; i--) {
      const historyLocation = _history[i];
      if (isListingDetailPath(historyLocation.pathname)) {
        continue;
      }
      if (historyLocation.pathname !== currentPathname) {
        return historyLocation;
      }
    }
    return undefined;
  }, [_history, location]);
  return /*#__PURE__*/_jsx(LocationHistoryContext.Provider, {
    value: {
      getBackLinkLocation
    },
    children: children
  });
}
export function useLocationHistory() {
  return useContext(LocationHistoryContext);
}